import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand } from '@fortawesome/free-solid-svg-icons';
import { BvIcon } from 'bevi-icon';
import { Spinner } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { SimulationBankService } from '../../../../../services/SimulationBank/SimulationBankService';
import ModalDefault from '../../../../ModalDefault/ModalDefault';
import IALogo from '../../../../../assets/img/IA.svg';
import FormatMoney from '../../../../../utils/FormatMoney';
import moment from 'moment';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import FinanceireLimitsHint from '../Step4Params/FinanceireLimitsHint';
import { SIM_TIPO_OPTIONS } from '../Step4Params/constants';
import { applyMoneyMask, parseMoney } from '../Step4Params/moneyUtils';
import { calculatePrazoRange } from '../Step4Params/prazoCalculations';
import { selectStylesResults } from './selectStylesResults';
import { OfertaSelecionada as OfertaRow } from '../../../../../models/SimulationBank/SimulationBankModels';
import styles from './Step4Results.module.scss';

const PARAMS_CACHE_KEY = '@beviajuda:sim-step4-params';

const saveParamsCache = (simTipo: any, valor: string, parcelas: string) => {
    try {
        localStorage.setItem(PARAMS_CACHE_KEY, JSON.stringify({ simTipo, valor, parcelas }));
    } catch {}
};

interface Step4ResultsProps {
    onRestart: () => void;
    onBack: () => void;
    onSimulate: () => void;
    onSimulateNewCPF?: () => void;
    onProsseguirDigitacao: (oferta: OfertaRow) => void;
    // Avisa o SimulationBank quando a tela de loading interna do cálculo de comissão
    // entra/sai — usado pra esconder o título/subtítulo da etapa nesse período (ver
    // isLoadingStep em SimulationBank.tsx, que não cobre esse loading porque `step`
    // não muda durante o cálculo de comissão).
    onCommissionLoadingChange?: (loading: boolean) => void;
}

interface Comissao {
    valor_comissao?: string;
    percentual_total?: number;
    erro?: number;
}

interface ErroFinanceira {
    financeira: string;
    erro: string;
}

export type SortDir = 'asc' | 'desc' | null;

const TABLE_COLUMNS = [
    { key: 'financeira', label: 'Financeira' },
    { key: 'valor_solicitado', label: 'Vlr. Solicitado' },
    { key: 'valor_parcela', label: 'Parcela' },
    { key: 'qtde_parcelas', label: 'Prazo' },
    { key: 'primeiro_vencimento', label: '1ª Parcela' },
    { key: 'seguro', label: 'Seguro' },
    { key: 'valor_seguro', label: 'Vlr. Seguro' },
    { key: 'codigo_domus', label: 'Código Domus' },
    { key: 'codigo_banco', label: 'Código Banco' },
];

// Financeira, Vlr. Solicitado, Parcela e Prazo ficam fixas (não rolam com o scroll horizontal).
const FROZEN_COLUMNS_COUNT = 4;

// Comissão (%) e Comissão (R$) continuam exclusivas da simulação de comissão (senha de
// relatório) — Código Domus/Código Banco já vêm no CONVENIO do response padrão da
// simulação, então saíram daqui e foram para TABLE_COLUMNS.
const COMMISSION_COLUMNS = [
    { key: 'percentual_total', label: 'Comissão (%)' },
    { key: 'valor_comissao', label: 'Comissão (R$)' },
];

const formatMoney = (value?: number): string => (value != null ? FormatMoney(value) : '—');
const formatDate = (value?: string): string => (value ? moment(value).format('DD/MM/YYYY') : '—');
const formatPercent = (value?: number): string => (value != null ? `${value.toFixed(2)}%` : '—');
const formatInsurance = (value?: string | boolean): string => {
    if (typeof value === 'string' && value) return value.toUpperCase();
    if (typeof value === 'boolean') return value ? 'SIM' : 'NÃO';
    return 'NÃO';
};

const formatCellValue = (column: string, row: OfertaRow): string => {
    switch (column) {
        case 'financeira':
            return row.financeira;
        case 'valor_solicitado':
            return formatMoney(row.valor_solicitado);
        case 'valor_parcela':
            return formatMoney(row.valor_parcela);
        case 'qtde_parcelas':
            return row.qtde_parcelas != null ? `${row.qtde_parcelas}x` : '—';
        case 'primeiro_vencimento':
            return formatDate(row.primeiro_vencimento);
        case 'seguro':
            return formatInsurance(row.seguro);
        case 'valor_seguro':
            return row.valor_seguro != null ? formatMoney(row.valor_seguro) : '—';
        case 'codigo_domus':
            return row.codigo_domus != null ? String(row.codigo_domus) : '—';
        case 'codigo_banco':
            return row.codigo_banco != null ? String(row.codigo_banco) : '—';
        default:
            return '—';
    }
};

const getSortValue = (column: string, row: OfertaRow, comissoes?: Record<string, Comissao>): string | number => {
    switch (column) {
        case 'financeira':
            return row.financeira ?? '';
        case 'valor_solicitado':
            return row.valor_solicitado ?? 0;
        case 'valor_parcela':
            return row.valor_parcela ?? 0;
        case 'qtde_parcelas':
            return row.qtde_parcelas ?? 0;
        case 'primeiro_vencimento':
            return row.primeiro_vencimento ?? '';
        case 'seguro':
            return String(row.seguro ?? '');
        case 'valor_seguro':
            return row.valor_seguro ?? 0;
        case 'codigo_domus':
            return row.codigo_domus ?? '';
        case 'codigo_banco':
            return row.codigo_banco ?? '';
        case 'percentual_total':
        case 'valor_comissao': {
            const com = comissoes?.[row.id];
            if (!com || com.erro) return '';
            const value = com[column];
            return value != null ? (column === 'valor_comissao' ? Number(value) : value) : '';
        }
        default:
            return '';
    }
};

const extractSeguroInfo = (oferta: any): { seguro: string | boolean | undefined; valor_seguro: number | undefined; descricao_seguro: string | undefined } => {
    const taxas = oferta.DETALHES_TAXAS ?? oferta.detalhes_taxas ?? {};
    const convenio = oferta.CONVENIO ?? oferta.convenio ?? {};

    const valorSeguro = taxas.VALOR_SEGURO ?? taxas.valor_seguro;
    const tituloSeguro = taxas.TITULO_SEGURO ?? taxas.titulo_seguro;
    const descricaoSeguro = taxas.DESCRICAO_SEGURO ?? taxas.descricao_seguro;
    const seguroField = oferta.SEGURO ?? oferta.seguro;
    const descConvenio = (convenio.DESCRICAO ?? convenio.descricao ?? '').toUpperCase();

    // Prioridade: titulo > campo direto > valor numérico > descrição convenio > undefined
    let seguro: string | boolean | undefined;

    if (tituloSeguro) {
        seguro = tituloSeguro;
    } else if (seguroField !== undefined) {
        seguro = seguroField;
    } else if (valorSeguro != null && valorSeguro > 0) {
        seguro = true;
    } else if (descConvenio) {
        if (descConvenio.includes('SEM SEGURO')) {
            seguro = false;
        } else if (descConvenio.includes('SEGURO')) {
            seguro = true;
        }
    }

    return { seguro, valor_seguro: valorSeguro, descricao_seguro: descricaoSeguro };
};

const flattenResultados = (resultados: any[]): { rows: OfertaRow[]; erros: ErroFinanceira[] } => {
    const rows: OfertaRow[] = [];
    const erros: ErroFinanceira[] = [];

    resultados.forEach((result: any) => {
        const financeira = result.FINANCEIRA ?? result.financeira ?? '—';
        const sucesso = result.SUCESSO ?? result.sucesso ?? true;

        if (!sucesso) {
            let mensagemErro = result.ERRO ?? result.erro ?? 'Não foi possível simular com esta financeira.';
            const regex = new RegExp(`^(${financeira}:\\s*)+`, 'i');
            mensagemErro = mensagemErro.replace(regex, '');
            erros.push({
                financeira,
                erro: mensagemErro,
            });
            return;
        }

        const ofertas = result.OFERTAS ?? result.ofertas ?? [];
        ofertas.forEach((oferta: any, idx: number) => {
            const { seguro, valor_seguro, descricao_seguro } = extractSeguroInfo(oferta);
            const id = oferta.ID ?? oferta.id ?? `${financeira}-${idx}`;
            const convenio = oferta.CONVENIO ?? oferta.convenio ?? {};

            rows.push({
                key: `${financeira}-${idx}`,
                id,
                financeira,
                qtde_parcelas: oferta.QTDE_PARCELAS ?? oferta.qtde_parcelas,
                valor_solicitado: oferta.VALOR_SOLICITADO ?? oferta.valor_solicitado,
                valor_parcela: oferta.VALOR_PARCELA ?? oferta.valor_parcela,
                primeiro_vencimento: oferta.PRIMEIRO_VENCIMENTO ?? oferta.primeiro_vencimento,
                seguro,
                valor_seguro,
                descricao_seguro,
                codigo_domus: convenio.CODIGO_INTERNO ?? convenio.codigo_interno,
                codigo_banco: convenio.CODIGO ?? convenio.codigo,
                descricao_convenio: convenio.NOME ?? convenio.nome,
            });
        });
    });

    return { rows, erros };
};

const flattenComissoes = (resultados: any[]): Record<string, Comissao> => {
    const map: Record<string, Comissao> = {};
    resultados.forEach((r: any) => {
        const ofertas: any[] = r.OFERTAS ?? r.ofertas ?? [];
        ofertas.forEach((o: any) => {
            const id = o.ID ?? o.id;
            const com = o.COMISSAO ?? o.comissao;
            if (id != null && com) {
                map[id] = {
                    valor_comissao: com.VALOR_COMISSAO ?? com.valor_comissao,
                    percentual_total: com.PERCENTUAL_TOTAL ?? com.percentual_total,
                    erro: com.ERRO ?? com.erro ?? 0,
                };
            }
        });
    });
    return map;
};

const COMMISSION_STATUS_MESSAGES = [
    'Estamos calculando os valores de comissão',
    'Conferindo as taxas de cada financeira',
    'Aplicando as regras de comissionamento',
    'Quase pronto!',
    'O resultado será exibido em instantes',
];

export const ArrowDown: FC<{ color: string }> = ({ color }) => (
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
        <path d="M3.94203 4.62759C3.85314 4.62759 3.76981 4.61092 3.69203 4.57759C3.61425 4.54425 3.54203 4.49425 3.47536 4.42759L0.175362 1.12759C0.05314 1.00536 -0.00519324 0.849809 0.00036232 0.66092C0.00591788 0.472031 0.0698067 0.316475 0.192029 0.194253C0.336473 0.0498081 0.494807 -0.0140804 0.667029 0.00258622C0.839251 0.0192529 0.992029 0.0886973 1.12536 0.21092L3.94203 3.02759L6.7587 0.21092C6.88092 0.0886973 7.03925 0.0220305 7.2337 0.0109194C7.42814 -0.000191687 7.58647 0.0664751 7.7087 0.21092C7.85314 0.333142 7.91703 0.48592 7.90036 0.669253C7.8837 0.852586 7.81425 1.01092 7.69203 1.14425L4.4087 4.42759C4.34203 4.49425 4.26981 4.54425 4.19203 4.57759C4.11425 4.61092 4.03092 4.62759 3.94203 4.62759Z" fill={color} />
    </svg>
);

interface SortIconProps {
    dir: SortDir;
    active: boolean;
}

export const SortIcon: FC<SortIconProps> = ({ dir, active }) => {
    if (!active || !dir) {
        return (
            <span className="sim-result-table__sort-icon">
                <span className={styles.sortIconWrapper}>
                    <span className={styles.sortArrowRotated}>
                        <ArrowDown color="#454787" />
                    </span>
                    <ArrowDown color="#454787" />
                </span>
            </span>
        );
    }

    return (
        <span className="sim-result-table__sort-icon sim-result-table__sort-icon--active">
            {dir === 'asc' ? (
                <ArrowDown color="#454787" />
            ) : (
                <span className={styles.sortArrowRotated}>
                    <ArrowDown color="#454787" />
                </span>
            )}
        </span>
    );
};

interface ResultSummaryProps {
    isTotal: boolean;
    valor?: number;
    tipoLabel: string;
    numParcelas?: number;
    prazoMin?: number;
    prazoMax?: number;
    selecionadas: any[];
    isEditing: boolean;
    editTipo: any;
    editValor: string;
    editParcelas: string;
    isLoadingSimulation: boolean;
    onEditChange: (tipo: any, valor: string, parcelas: string) => void;
    onEdit: () => void;
    onSimulate: () => Promise<void>;
}

const ResultSummary: FC<ResultSummaryProps> = ({
    isTotal,
    valor,
    tipoLabel,
    numParcelas,
    prazoMin,
    prazoMax,
    selecionadas,
    isEditing,
    editTipo,
    editValor,
    editParcelas,
    isLoadingSimulation,
    onEditChange,
    onEdit,
    onSimulate,
}) => {
    const handleSubmit = async () => {
        await onSimulate();
    };

    const isButtonDisabled = isEditing && (isLoadingSimulation || !editParcelas || !editValor);
    const valorLabel = isEditing && editTipo?.value === 'total'
        ? 'total desejado'
        : isEditing
            ? 'de parcela'
            : isTotal
                ? 'total desejado'
                : 'de parcela';

    return (
        <div className={styles.summaryGrid}>
            <div className={styles.summaryField}>
                <label className={styles.summaryLabel}>Tipo de simulação*</label>
                {isEditing ? (
                    <ReactSelect
                        options={SIM_TIPO_OPTIONS}
                        value={editTipo}
                        styles={selectStylesResults}
                        placeholder="Selecione..."
                        noOptionsMessage={() => 'Sem opções'}
                        onChange={(v) => onEditChange(v, editValor, editParcelas)}
                    />
                ) : (
                    <div className={styles.summaryValue}>{tipoLabel}</div>
                )}
            </div>

            <div className={styles.summaryField}>
                <label className={styles.summaryLabel}>Valor {valorLabel}*</label>
                {isEditing ? (
                    <input
                        type="text"
                        placeholder="0.000,00"
                        value={editValor}
                        onChange={(e) => onEditChange(editTipo, applyMoneyMask(e.target.value), editParcelas)}
                        className={styles.summaryInput}
                    />
                ) : (
                    <div className={styles.summaryValue}>
                        {valor != null ? formatMoney(valor) : '—'}
                    </div>
                )}
                <FinanceireLimitsHint financeiras={selecionadas} isTotal={isTotal} />
            </div>

            <div className={styles.summaryField}>
                <label className={styles.summaryLabel}>Parcelas*</label>
                <div className={styles.parcelasFieldContainer}>
                    {isEditing ? (
                        <input
                            type="text"
                            placeholder="Digite a quantidade de parcelas"
                            value={editParcelas}
                            onChange={(e) => onEditChange(editTipo, editValor, e.target.value.replace(/\D/g, ''))}
                            className={`${styles.summaryInput} ${styles.parcelasInput}`}
                        />
                    ) : (
                        <div className={`${styles.summaryValue} ${styles.parcelasValue}`}>
                            {numParcelas ? `${numParcelas}x` : '—'}
                        </div>
                    )}
                    <button
                        onClick={isEditing ? handleSubmit : onEdit}
                        disabled={isButtonDisabled}
                        className={styles.alterButton}
                    >
                        {isEditing ? (
                            isLoadingSimulation ? <Spinner size="sm" /> : 'Simular'
                        ) : (
                            <>Alterar </>
                        )}
                    </button>
                </div>
                {prazoMin != null && prazoMax != null && (
                    <span className={styles.parcelasHint}>
                        {Math.floor(prazoMin)} a {Math.ceil(prazoMax)}*
                    </span>
                )}
            </div>
        </div>
    );
};

const service = new SimulationBankService();

const Step4Results: FC<Step4ResultsProps> = ({ onRestart, onBack, onSimulate, onSimulateNewCPF, onProsseguirDigitacao, onCommissionLoadingChange }) => {
    const { resultados, simParams, financeiras, setSimParams, handleSimulate, isLoadingSimulation, screeningId } = useSimulationBank();
    const [sortCol, setSortCol] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editTipo, setEditTipo] = useState<any>(null);
    const [editValor, setEditValor] = useState('');
    const [editParcelas, setEditParcelas] = useState('');
    const [ofertaSelecionada, setOfertaSelecionada] = useState<string | null>(null);

    const [comissoes, setComissoes] = useState<Record<string, Comissao>>({});
    const [showCommissionModal, setShowCommissionModal] = useState(false);
    const [password, setPassword] = useState('');
    const [commissionError, setCommissionError] = useState<string | null>(null);
    const [commissionLoading, setCommissionLoading] = useState(false);
    const [commissionStatusIdx, setCommissionStatusIdx] = useState(0);
    const [descricaoModal, setDescricaoModal] = useState<string | null>(null);
    const [showFullscreenTable, setShowFullscreenTable] = useState(false);
    const [descricaoTooltip, setDescricaoTooltip] = useState<{ text: string; top: number; left: number } | null>(null);
    const fullscreenOpenedRef = useRef(false);

    useEffect(() => {
        onCommissionLoadingChange?.(commissionLoading);
    }, [commissionLoading, onCommissionLoadingChange]);

    // Ao desmontar (ex.: usuário sai da tela no meio do cálculo), garante que o
    // título/subtítulo da etapa voltem — sem isso ficariam escondidos pra sempre.
    useEffect(() => () => onCommissionLoadingChange?.(false), []);

    const showDescricaoTooltip = (e: React.MouseEvent<HTMLButtonElement>, text: string) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setDescricaoTooltip({ text, top: rect.top - 8, left: rect.left + rect.width / 2 });
    };
    const hideDescricaoTooltip = () => setDescricaoTooltip(null);
    const [commissionCharCount, setCommissionCharCount] = useState(0);

    const toggleOfertaSelecionada = (rowKey: string) => {
        setOfertaSelecionada(ofertaSelecionada === rowKey ? null : rowKey);
    };

    useEffect(() => {
        const resultadosFonte = Array.isArray(resultados) ? resultados : [];
        if (resultadosFonte.length > 0 && !fullscreenOpenedRef.current) {
            fullscreenOpenedRef.current = true;
            setShowFullscreenTable(true);
        }
    }, [resultados]);

    useEffect(() => {
        if (!commissionLoading) {
            setCommissionStatusIdx(0);
            setCommissionCharCount(0);
            return;
        }

        let cancelled = false;
        let charTimer: ReturnType<typeof setTimeout>;
        let holdTimer: ReturnType<typeof setTimeout>;

        const typeMessage = (idx: number) => {
            const message = COMMISSION_STATUS_MESSAGES[idx];
            let count = 0;

            const tick = () => {
                if (cancelled) return;
                count = Math.min(count + 3, message.length);
                setCommissionCharCount(count);
                if (count < message.length) {
                    charTimer = setTimeout(tick, 30);
                } else {
                    holdTimer = setTimeout(() => {
                        if (cancelled) return;
                        const next = (idx + 1) % COMMISSION_STATUS_MESSAGES.length;
                        setCommissionStatusIdx(next);
                        setCommissionCharCount(0);
                        typeMessage(next);
                    }, 1500);
                }
            };
            tick();
        };

        typeMessage(0);

        return () => {
            cancelled = true;
            clearTimeout(charTimer);
            clearTimeout(holdTimer);
        };
    }, [commissionLoading]);

    const selecionadas = financeiras.filter((f) => f.selecionada);
    const isTotal = simParams?.valor_desejado != null;
    const valor = isTotal ? simParams?.valor_desejado : simParams?.valor_parcela;
    const tipoLabel = isTotal ? 'Por valor total desejado' : 'Por valor de parcela';

    const { prazoMin, prazoMax } = useMemo(
        () => calculatePrazoRange(selecionadas),
        [selecionadas]
    );

    const resultadosFonte = Array.isArray(resultados) ? resultados : [];
    const { rows: baseRows, erros } = flattenResultados(resultadosFonte);

    const rows = useMemo(() => {
        if (!sortCol || !sortDir) return baseRows;

        return [...baseRows].sort((a, b) => {
            const va = getSortValue(sortCol, a, comissoes);
            const vb = getSortValue(sortCol, b, comissoes);
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), undefined, { numeric: true });
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [baseRows, sortCol, sortDir, comissoes]);

    const { minParcela, maxParcela, minSeguro, maxSeguro, minPercentual, maxPercentual, minComissao, maxComissao } = useMemo(() => {
        const parcelaValues = baseRows.map((r) => r.valor_parcela).filter((v): v is number => v != null);
        const seguroValues = baseRows
            .filter((r) => r.seguro !== false && r.seguro !== 'NÃO' && r.seguro != null)
            .map((r) => r.valor_seguro)
            .filter((v): v is number => v != null);
        const comissoesValidas = baseRows.map((r) => comissoes[r.id]).filter((c): c is Comissao => !!c && !c.erro);
        const percentualValues = comissoesValidas.map((c) => c.percentual_total).filter((v): v is number => v != null);
        const comissaoValues = comissoesValidas.map((c) => Number(c.valor_comissao)).filter((v): v is number => !Number.isNaN(v));

        return {
            minParcela: parcelaValues.length ? Math.min(...parcelaValues) : undefined,
            maxParcela: parcelaValues.length ? Math.max(...parcelaValues) : undefined,
            minSeguro: seguroValues.length ? Math.min(...seguroValues) : undefined,
            maxSeguro: seguroValues.length ? Math.max(...seguroValues) : undefined,
            minPercentual: percentualValues.length ? Math.min(...percentualValues) : undefined,
            maxPercentual: percentualValues.length ? Math.max(...percentualValues) : undefined,
            minComissao: comissaoValues.length ? Math.min(...comissaoValues) : undefined,
            maxComissao: comissaoValues.length ? Math.max(...comissaoValues) : undefined,
        };
    }, [baseRows, comissoes]);

    const hasComissoes = Object.keys(comissoes).length > 0;

    const handleSort = (col: string) => {
        if (sortCol !== col) {
            setSortCol(col);
            setSortDir('asc');
            return;
        }
        if (sortDir === 'asc') {
            setSortDir('desc');
            return;
        }
        setSortCol(null);
        setSortDir(null);
    };

    const handleBackWhileEditing = () => {
        if (isEditing && editTipo && editValor && editParcelas) {
            saveParamsCache(editTipo, editValor, editParcelas);
        }
        onBack();
    };

    const handleEdit = () => {
        setIsEditing(true);
        setEditTipo(isTotal ? SIM_TIPO_OPTIONS[0] : SIM_TIPO_OPTIONS[1]);
        setEditValor(valor != null ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
        setEditParcelas(String(simParams?.num_parcelas || ''));
    };

    const handleEditChange = (tipo: any, valorStr: string, parcelas: any) => {
        setEditTipo(tipo);
        setEditValor(valorStr);
        setEditParcelas(parcelas);
    };

    const handleSimulateEdit = async () => {
        if (!editParcelas || !editValor) return;

        saveParamsCache(editTipo, editValor, editParcelas);

        const params = {
            financeiras: selecionadas.map((f) => f.canonical_id ?? String(f.id)),
            num_parcelas: parseInt(editParcelas, 10),
            ...(editTipo.value === 'total'
                ? { valor_desejado: parseMoney(editValor) }
                : { valor_parcela: parseMoney(editValor) }),
        };
        setSimParams(params);
        onSimulate();
        await handleSimulate(params);
        setIsEditing(false);
    };

    const openCommissionModal = () => {
        setPassword('');
        setCommissionError(null);
        setShowCommissionModal(true);
    };

    const closeCommissionModal = () => {
        setShowCommissionModal(false);
        setPassword('');
        setCommissionError(null);
    };

    const submitCommission = async () => {
        if (screeningId == null || !password) return;
        setShowCommissionModal(false);
        setCommissionLoading(true);
        setCommissionError(null);
        const [response, error] = await service.getCommission({
            journey_id: String(screeningId),
            password_report: password,
        });
        setCommissionLoading(false);

        if (error) {
            setCommissionError(response?.message || error);
            setShowCommissionModal(true);
            return;
        }

        const resultadosComissao = response?.data?.data?.RESULTADOS
            ?? response?.data?.RESULTADOS
            ?? response?.RESULTADOS
            ?? [];
        setComissoes(flattenComissoes(Array.isArray(resultadosComissao) ? resultadosComissao : []));
        setPassword('');
    };

    if (commissionLoading) {
        return (
            <div className="step-loading">
                <div className="step-loading__logo">
                    <img src={IALogo} alt="IA" />
                </div>
                <p className="step-loading__title">Estamos processando<br />a sua simulação de comissão</p>
                <p className="step-loading__status" key={commissionStatusIdx}>
                    {COMMISSION_STATUS_MESSAGES[commissionStatusIdx].slice(0, commissionCharCount)}
                </p>
                <div className="step-loading__dots">
                    <span /><span /><span />
                </div>
            </div>
        );
    }

    const renderTable = (isFullscreen: boolean = false) => (
        <table className="sim-result-table">
            <tbody>
                <tr>
                    {isFullscreen ? (
                        <th colSpan={TABLE_COLUMNS.length} className="sim-result-table__th sim-result-table__th--group">Simulação</th>
                    ) : (
                        <>
                            <th colSpan={FROZEN_COLUMNS_COUNT} className="sim-result-table__th sim-result-table__th--group sim-result-table__th--frozen-group">Simulação</th>
                            <th colSpan={TABLE_COLUMNS.length - FROZEN_COLUMNS_COUNT} className="sim-result-table__th sim-result-table__th--group" />
                        </>
                    )}
                    {isFullscreen && !hasComissoes && (
                        <th rowSpan={2} className="sim-result-table__th sim-result-table__th--group sim-result-table__th--digitacao-header">Digitação</th>
                    )}
                    {hasComissoes ? (
                        <th colSpan={COMMISSION_COLUMNS.length} className="sim-result-table__th sim-result-table__th--group sim-result-table__th--commission-group bg-primary opacity-9">Comissão</th>
                    ) : (
                        <th
                            rowSpan={2 + rows.length}
                            className="sim-result-table__th sim-result-table__th--group sim-result-table__commission-action"
                            onClick={openCommissionModal}
                            title="Simular comissão"
                        >
                            <div className="sim-result-table__commission-action-inner">
                                <button
                                    type="button"
                                    className="sim-commission__simular-btn sim-commission__simular-btn--lg"
                                    tabIndex={-1}
                                >
                                    <BvIcon name="calculate" color="#7779BB" size={1.8} />
                                </button>
                                <p className="sim-commission__simular-caption">Entre para simular a sua comissão</p>
                            </div>
                        </th>
                    )}
                    {isFullscreen && hasComissoes && (
                        <th rowSpan={2} className="sim-result-table__th sim-result-table__th--group sim-result-table__th--digitacao-header">Digitação</th>
                    )}
                </tr>
                <tr>
                    {TABLE_COLUMNS.map((column, colIdx) => (
                        <th
                            key={column.key}
                            className={`sim-result-table__th sim-result-table__th--sortable ${!isFullscreen && colIdx < FROZEN_COLUMNS_COUNT ? `sim-result-table__th--frozen sim-result-table__th--frozen-${colIdx}` : ''} ${colIdx === TABLE_COLUMNS.length - 1 && hasComissoes ? 'sim-result-table__th--divider' : ''}`}
                            onClick={() => handleSort(column.key)}
                        >
                            {column.label}
                            <SortIcon dir={sortDir} active={sortCol === column.key} />
                        </th>
                    ))}
                    {hasComissoes && (
                        COMMISSION_COLUMNS.map((column) => (
                            <th
                                key={column.key}
                                className="sim-result-table__th sim-result-table__th--sortable sim-result-table__th--commission-pct bg-primary opacity-9"
                                onClick={() => handleSort(column.key)}
                            >
                                {column.label}
                                <SortIcon dir={sortDir} active={sortCol === column.key} />
                            </th>
                        ))
                    )}
                </tr>
                {rows.length === 0 && (
                    <tr>
                        <td colSpan={TABLE_COLUMNS.length + 1 + (hasComissoes ? COMMISSION_COLUMNS.length : 1)} className="sim-result-table__empty">
                            Nenhum resultado disponível.
                        </td>
                    </tr>
                )}
                {rows.map((row, idx) => {
                    const com = comissoes[row.id];
                    return (
                        <tr key={row.key} className="sim-result-table__row">
                            {TABLE_COLUMNS.map((column, colIdx) => (
                                <td
                                    key={column.key}
                                    className={`sim-result-table__td ${!isFullscreen && colIdx < FROZEN_COLUMNS_COUNT ? `sim-result-table__td--frozen sim-result-table__td--frozen-${colIdx}` : ''} ${colIdx === TABLE_COLUMNS.length - 1 && hasComissoes ? 'sim-result-table__td--divider' : ''} ${idx % 2 !== 0 ? styles.tableRowAlternate : ''}`}
                                >
                                    {column.key === 'financeira' ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {!isFullscreen && (
                                                <div className="form-check" style={{ margin: 0, padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`oferta-${row.key}`}
                                                        checked={ofertaSelecionada === row.key}
                                                        onChange={() => toggleOfertaSelecionada(row.key)}
                                                        title="Selecionar oferta"
                                                        style={{ margin: 0 }}
                                                    />
                                                </div>
                                            )}
                                            {formatCellValue(column.key, row)}
                                        </span>
                                    ) : column.key === 'seguro' && row.descricao_seguro ? (
                                        <span className="sim-result-table__seguro-cell">
                                            {formatCellValue(column.key, row)}
                                            <button
                                                type="button"
                                                className="sim-result-table__info-btn"
                                                onClick={() => setDescricaoModal(row.descricao_seguro!)}
                                                onMouseEnter={(e) => showDescricaoTooltip(e, row.descricao_seguro!)}
                                                onMouseLeave={hideDescricaoTooltip}
                                            >
                                                <BvIcon name="info" variant="dark" width={16} height={16} />
                                            </button>
                                        </span>
                                    ) : column.key === 'codigo_domus' && row.descricao_convenio ? (
                                        <span className="sim-result-table__seguro-cell">
                                            {formatCellValue(column.key, row)}
                                            <button
                                                type="button"
                                                className="sim-result-table__info-btn"
                                                onClick={() => setDescricaoModal(row.descricao_convenio!)}
                                                onMouseEnter={(e) => showDescricaoTooltip(e, row.descricao_convenio!)}
                                                onMouseLeave={hideDescricaoTooltip}
                                            >
                                                <BvIcon name="info" variant="dark" width={16} height={16} />
                                            </button>
                                        </span>
                                    ) : column.key === 'valor_parcela' || column.key === 'valor_seguro' ? (
                                        (() => {
                                            const value = column.key === 'valor_parcela' ? row.valor_parcela : row.valor_seguro;
                                            const min = column.key === 'valor_parcela' ? minParcela : minSeguro;
                                            const max = column.key === 'valor_parcela' ? maxParcela : maxSeguro;
                                            const showBadges = value != null && min !== max;
                                            return (
                                                <span className="sim-result-table__value-highlight">
                                                    {formatCellValue(column.key, row)}
                                                    {showBadges && value === max && (
                                                        <span className="sim-result-table__badge sim-result-table__badge--maior">MAIOR</span>
                                                    )}
                                                    {showBadges && value === min && (
                                                        <span className="sim-result-table__badge sim-result-table__badge--menor">MENOR</span>
                                                    )}
                                                </span>
                                            );
                                        })()
                                    ) : (
                                        formatCellValue(column.key, row)
                                    )}
                                </td>
                            ))}
                            {isFullscreen && !hasComissoes && (
                                <td className={`sim-result-table__td sim-result-table__td--digitacao ${idx % 2 !== 0 ? styles.tableRowAlternate : ''}`}>
                                    <button
                                        type="button"
                                        className="sim-result-table__digitacao-btn"
                                        onClick={() => onProsseguirDigitacao(row)}
                                        title="Digitar proposta"
                                    >
                                        Digitar proposta
                                    </button>
                                </td>
                            )}
                            {hasComissoes ? (
                                <>
                                    <td className={`sim-result-table__td sim-result-table__td--commission-pct ${idx % 2 !== 0 ? styles.tableRowAlternate : ''}`}>
                                        {com && !com.erro && com.percentual_total != null ? (
                                            <span className="sim-result-table__value-highlight">
                                                {formatPercent(com.percentual_total)}
                                                {minPercentual !== maxPercentual && com.percentual_total === maxPercentual && (
                                                    <span className="sim-result-table__badge sim-result-table__badge--maior">MAIOR</span>
                                                )}
                                                {minPercentual !== maxPercentual && com.percentual_total === minPercentual && (
                                                    <span className="sim-result-table__badge sim-result-table__badge--menor">MENOR</span>
                                                )}
                                            </span>
                                        ) : '—'}
                                    </td>
                                    <td className={`sim-result-table__td sim-result-table__td--commission-total ${idx % 2 !== 0 ? styles.tableRowAlternate : ''}`}>
                                        {com && !com.erro && com.valor_comissao != null ? (() => {
                                            const value = Number(com.valor_comissao);
                                            return (
                                                <span className="sim-result-table__value-highlight">
                                                    {formatMoney(value)}
                                                    {minComissao !== maxComissao && value === maxComissao && (
                                                        <span className="sim-result-table__badge sim-result-table__badge--maior">MAIOR</span>
                                                    )}
                                                    {minComissao !== maxComissao && value === minComissao && (
                                                        <span className="sim-result-table__badge sim-result-table__badge--menor">MENOR</span>
                                                    )}
                                                </span>
                                            );
                                        })() : '—'}
                                    </td>
                                </>
                            ) : null}
                            {isFullscreen && hasComissoes && (
                                <td className={`sim-result-table__td sim-result-table__td--digitacao ${idx % 2 !== 0 ? styles.tableRowAlternate : ''}`}>
                                    <button
                                        type="button"
                                        className="sim-result-table__digitacao-btn"
                                        onClick={() => onProsseguirDigitacao(row)}
                                        title="Digitar proposta"
                                    >
                                        Digitar proposta
                                    </button>
                                </td>
                            )}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <div className="step-results-v2">
            <ResultSummary
                isTotal={isTotal}
                valor={valor}
                tipoLabel={tipoLabel}
                numParcelas={simParams?.num_parcelas}
                prazoMin={prazoMin}
                prazoMax={prazoMax}
                selecionadas={selecionadas}
                isEditing={isEditing}
                editTipo={editTipo}
                editValor={editValor}
                editParcelas={editParcelas}
                isLoadingSimulation={isLoadingSimulation}
                onEditChange={handleEditChange}
                onEdit={handleEdit}
                onSimulate={handleSimulateEdit}
            />

            <p className="step-results-v2__lead">
                As ofertas abaixo foram obtidas diretamente nas instituições financeiras. Caso a <strong>quantidade de parcelas</strong> informada não esteja disponível, <strong>será apresentada a opção mais próxima encontrada.</strong>
            </p>

            <div className="step-results-v2__disclaimer">
                <span className={styles.disclaimerIcon}>
                    <BvIcon name="info" variant="dark" width={16} height={16} />
                </span>
                As taxas apresentadas são estimativas referenciais e podem variar. A taxa se fixa somente após a digitação da proposta.
            </div>

            <div className="sim-result-table-block">
                <div className="sim-result-table-actions">
                    <button
                        type="button"
                        className="sim-result-table-commission-btn"
                        onClick={() => setShowFullscreenTable(true)}
                        aria-label="Visualizar tabela em tela cheia"
                    >
                        <FontAwesomeIcon icon={faExpand} />
                        <span>Ver Comissão</span>
                    </button>
                    <button
                        type="button"
                        className="sim-result-table-fullscreen-btn"
                        onClick={() => setShowFullscreenTable(true)}
                        aria-label="Visualizar tabela em tela cheia"
                    >
                        <FontAwesomeIcon icon={faExpand} />
                        <span>Tela cheia</span>
                    </button>
                </div>

                <div className="sim-result-table-wrap">
                    {renderTable()}
                </div>
            </div>

            {rows.length > 0 && (
                <p className="step-results-v2__lead" style={{ marginTop: '24px', marginBottom: '0' }}>
                    Selecione apenas uma financeira para prosseguir.<br />
                    Você poderá avançar com a digitação de apenas uma simulação.
                </p>
            )}

            <ModalDefault
                title="Visualização da tabela"
                show={showFullscreenTable}
                onClose={() => setShowFullscreenTable(false)}
                sizeModal="xxl"
                showFooter={false}
                scrollable
                className="sim-result-table-modal"
            >
                <div className="sim-result-table-wrap">
                    {renderTable(true)}
                </div>
            </ModalDefault>

            {descricaoTooltip && createPortal(
                <div
                    className="sim-result-table__info-tooltip-portal"
                    style={{ top: descricaoTooltip.top, left: descricaoTooltip.left }}
                >
                    {descricaoTooltip.text}
                </div>,
                document.body
            )}

            <ModalDefault
                className="sim-commission-modal-dialog"
                title="Simular Comissão"
                show={showCommissionModal}
                onClose={closeCommissionModal}
                sizeModal="sm"
                showFooter={false}
            >
                <p className="sim-commission-modal__text">
                    Insira a senha relatório no campo abaixo para realizar a simulação da comissão.
                </p>
                <input
                    type="password"
                    className="form-control"
                    placeholder="Senha relatório"
                    value={password}
                    autoFocus
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && password) submitCommission(); }}
                />
                {commissionError && (
                    <p className="sim-commission-modal__error">{commissionError}</p>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                    <button type="button" className="btn btn-default btn-text" onClick={closeCommissionModal}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-text btn-primary"
                        onClick={submitCommission}
                        disabled={!password}
                    >
                        Simular
                    </button>
                </div>
            </ModalDefault>

            <ModalDefault
                className="sim-descricao-modal-dialog"
                title="Descrição"
                show={!!descricaoModal}
                onClose={() => setDescricaoModal(null)}
                sizeModal="sm"
                showFooter={false}
            >
                <p className="sim-commission-modal__text">{descricaoModal}</p>
            </ModalDefault>

            {erros.length > 0 && (
                <div className="sim-result-errors">
                        <p className="sim-result-errors__lead">
                            As seguintes financeiras apresentaram o seguinte erro:
                        </p>
                    <ul className="sim-result-errors__list">
                        {erros.map((error) => (
                            <li key={error.financeira} className="sim-result-errors__item">
                                <BvIcon name="warning" size={1} />
                                <span className="sim-result-errors__financeira">{error.financeira}:</span>
                                <span className="sim-result-errors__msg">{error.erro}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <StepNavigation
                onBack={handleBackWhileEditing}
                onNext={() => {
                    const oferta = rows.find((r) => r.key === ofertaSelecionada);
                    if (oferta) onProsseguirDigitacao(oferta);
                }}
                nextLabel="Prosseguir para digitação"
                nextDisabled={!ofertaSelecionada}
                extraButton={{
                    label: 'Finalizar',
                    onClick: onRestart,
                }}
                secondExtraButton={onSimulateNewCPF ? {
                    label: 'Simular outro CPF',
                    onClick: onSimulateNewCPF,
                } : undefined}
            />
        </div>
    );
};

export default Step4Results;
